/**
 * MUSIC LIBRARY — the horizontal track selector across the top of the workspace.
 *
 * This is what replaces "close the drawer, find the next song, open it again".
 * In a production tool the whole library is within reach while you work on one
 * track, so switching songs is one click and never leaves the workspace.
 *
 * Each entry is a link rather than a button: it navigates to /library/:songId, so
 * middle-click and Back behave the way they do everywhere else in the app, and the
 * URL stays the source of truth for which song is open.
 *
 * The strip scrolls horizontally and is deliberately short — artwork, title, and
 * the length. Anything more turns a switcher into a second library page.
 */
import { Music } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SongArtwork } from '../songs';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface LibraryStripProps {
  songs: Song[];
  /** The song open in the workspace. */
  currentId: string;
  /** Builds the href for a song. Passed in so the route table stays in routes/. */
  hrefFor: (songId: string) => string;
  /**
   * What this set of tracks is, named for the user.
   *
   * Defaults to "Music library", which is what the strip has always been and still is
   * whenever the workspace was reached from the Library. It is overridden when the visit
   * belongs to a Studio project, because then these are the PROJECT's tracks and calling
   * them the music library would describe a set that is not on screen — see
   * routes/Studio.tsx.
   *
   * One string for both the eyebrow and the list's accessible name, deliberately: two
   * props would let the label a sighted user reads and the one announced diverge.
   */
  heading?: string;
}

export default function LibraryStrip({
  songs,
  currentId,
  hrefFor,
  heading = 'Music library',
}: LibraryStripProps) {
  return (
    <div className="lyx-ws-library">
      <p className="lyx-ws-eyebrow">{heading}</p>
      {/* A list, so a screen reader announces how many tracks there are and where
          in them the current one sits. */}
      <ul className="lyx-ws-strip" aria-label={heading}>
        {songs.map((song) => {
          const current = song.id === currentId;
          return (
            <li key={song.id} style={{ listStyle: 'none' }}>
              <Link
                to={hrefFor(song.id)}
                // The one piece of state this control has, and the reason it is
                // announced rather than only drawn in the accent colour.
                aria-current={current ? 'true' : undefined}
                className="lyx-ws-strip-item"
                style={{
                  border: `1px solid ${current ? c.accentBorder : c.border}`,
                  background: current ? c.accentSoft : c.surface1,
                }}
              >
                <SongArtwork
                  song={song}
                  height={36}
                  radius={7}
                  style={{ width: 36, flexShrink: 0, overflow: 'hidden' }}
                >
                  <Music
                    size={12}
                    color="rgba(255,255,255,0.5)"
                    style={{ position: 'absolute', bottom: 4, left: 5 }}
                  />
                </SongArtwork>
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 500,
                      color: current ? c.text1 : c.text2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {song.title}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 10,
                      color: c.text2,
                      fontFamily: 'IBM Plex Mono',
                      marginTop: 1,
                    }}
                  >
                    {song.length}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
